import { Fragment, useMemo, useState } from "react";
import { BarChart, Bar, Cell, LineChart, Line, Legend, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as ReTooltip } from "recharts";
import { Check, X, Info, TrendingDown, TrendingUp, Globe, Wrench, MessageSquareQuote, Eye, Settings2, ChevronDown, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { TruncatedText } from "@/components/ui/truncated-text";
import { cn } from "@/lib/utils";
import { useGetBrandIntelligenceAiVisibility, useSaveAiCompetitorSet, type BiAiPrompt, type BiAiGap } from "@/resources/brand-intelligence/brand-intelligence.api";
import { BI_TOOLTIPS } from "./tooltips";
import { TabHeader } from "@/components/shared/TabHeader";

const titleCase = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const humanize = (s: string | null) => (s ? s.replace(/_/g, " ") : "—");
// AUT-347: prompt-type labels display in Title Case ("use_case" -> "Use Case") in the chart + Type column.
const titleCaseWords = (s: string | null) => (s ? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—");
const fmtPct = (n: number | null | undefined) => (n == null || !Number.isFinite(n) ? "—" : `${n.toFixed(1)}%`);

// Most frequent non-null value (ties broken by first-seen order); null if none.
function mode(values: (string | null)[]): string | null {
  const counts = new Map<string, number>();
  for (const v of values) {
    if (v == null) continue;
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestN = 0;
  for (const [v, n] of counts) {
    if (n > bestN) {
      best = v;
      bestN = n;
    }
  }
  return best;
}

type PromptGroup = {
  key: string;
  query: string;
  type: string | null;
  category: string;
  platforms: BiAiPrompt[];
  appearedAny: boolean;
  usableAny: boolean;
  competitors: Set<string>;
};

const PLATFORM_TINT: Record<string, string> = {
  gemini: "bg-sky-500",
  chatgpt: "bg-emerald-500",
  claude: "bg-orange-500",
  perplexity: "bg-slate-700",
};
const platformCode = (p: string) => ({ gemini: "GEM", chatgpt: "GPT", claude: "CLA", perplexity: "PPX" })[p] ?? p.slice(0, 3).toUpperCase();

const TYPE_COLOR: Record<string, string> = {
  category: "hsl(var(--chart-1))",
  use_case: "hsl(var(--chart-2))",
  comparison: "hsl(var(--chart-3))",
  reputation: "hsl(var(--chart-4))",
};
// AUT-355: neutral/reputation/monitor_only had no dedicated tier in the old palette
// (they used the removed --status-weak-bg); the mockup's own AiVisibilityTab.tsx resolves all
// three to the warning-soft tier, not a neutral/muted one, so this follows the mockup rather
// than the SPIScorecard's muted-surface pick (that badge had no mockup counterpart to check).
const SENTIMENT_BADGE: Record<string, string> = {
  positive: "bg-status-success-soft",
  neutral: "bg-status-warning-soft",
  negative: "bg-status-error-soft",
};
const TYPE_BADGE: Record<string, string> = {
  category: "bg-status-info-soft",
  use_case: "bg-status-success-soft",
  comparison: "bg-status-warning-soft",
  reputation: "bg-status-warning-soft",
};
const GAP_ICON: Record<string, typeof Globe> = {
  content_gap: Wrench,
  reputation_gap: Eye,
  keyword_mismatch: MessageSquareQuote,
};
const GAP_ACTION_BADGE: Record<string, string> = {
  content_strategy: "bg-status-warning-soft",
  monitor_only: "bg-status-warning-soft",
};

// Line colors per brand (tracked brand = primary, competitors = chart palette).
const LINE_COLORS = ["hsl(var(--chart-2))", "hsl(261 55% 50%)", "hsl(var(--chart-4))", "hsl(var(--chart-5))", "hsl(var(--chart-3))"];
const lineColor = (brand: string, tracked: string, idx: number) => (brand === tracked ? "hsl(var(--primary))" : LINE_COLORS[idx % LINE_COLORS.length]);

/**
 * AUT-481: the brands whose series has exactly **one** non-null month.
 *
 * Recharts needs two points to stroke a line, so a single-month series drew nothing at all under
 * `dot={false}` — the brand sat in the legend with a value, and the chart showed empty space until
 * the user happened to hover the right pixel. Those series get a visible dot; every other series
 * keeps `dot={false}`, which is what stops a sixteen-brand chart turning into confetti.
 *
 * Exported because this is the whole of the fix that can be tested: recharts renders nothing
 * measurable under jsdom (`ResponsiveContainer` resolves to 0×0), so a test asserting the drawn dot
 * would assert nothing.
 */
export function singlePointSeries(
  data: { month: string; [k: string]: number | string | null }[],
  brands: string[],
): Set<string> {
  const counts = new Map<string, number>();

  for (const row of data) {
    for (const brand of brands) {
      const value = row[brand];
      if (value !== null && value !== undefined) {
        counts.set(brand, (counts.get(brand) ?? 0) + 1);
      }
    }
  }

  return new Set([...counts.entries()].filter(([, n]) => n === 1).map(([brand]) => brand));
}

function TrendChart({
  title,
  tip,
  data,
  brands,
  tracked,
  platforms,
  activePlatform,
  onPlatform,
}: {
  title: string;
  tip: string;
  data: { month: string; [k: string]: number | string | null }[];
  brands: string[];
  tracked: string;
  platforms?: string[];
  activePlatform?: string;
  onPlatform?: (p: string) => void;
}) {
  const singlePointBrands = useMemo(() => singlePointSeries(data, brands), [data, brands]);

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4">
          <CardHeading title={title} tip={tip} />
          {platforms && onPlatform && (
            <div className="flex flex-wrap items-center justify-end gap-1.5">
              {["all", ...platforms].map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => onPlatform(p)}
                  className={cn(
                    "h-7 rounded-full border px-3 text-xs font-medium transition-colors",
                    activePlatform === p ? "border-primary bg-primary/10 text-foreground" : "border-border bg-card text-muted-foreground hover:bg-primary/10 hover:text-foreground",
                  )}
                >
                  {p === "all" ? "All platforms" : titleCase(p)}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="mt-4 h-[300px] w-full">
          {data.length < 2 ? (
            <div className="h-full flex items-center justify-center text-xs text-muted-foreground">Not enough history yet — trends build up over monthly refreshes.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis width={40} tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} stroke="hsl(var(--muted-foreground))" />
                <ReTooltip
                  contentStyle={{ backgroundColor: "hsl(var(--card))", borderColor: "hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                  // AUT-482: both axes are percentages -- the Y axis already says so, and the
                  // tooltip read as a bare number.
                  formatter={(value: number | string) => `${value}%`}
                  // AUT-479: with 16 brands the tooltip is taller than its card and runs into the
                  // section below. The card does not clip it; recharts' wrapper simply carries no
                  // z-index, so the next Card paints over it by document order. This is the whole
                  // fix.
                  //
                  // Deliberately NOT `allowEscapeViewBox={{ y: true }}`: that branch of
                  // `getTooltipTranslateXY` returns `coordinate.y + offset` with no clamp at all,
                  // so hovering near the x-axis would drop a ~330px tooltip ~330px below the plot
                  // and off the bottom of the viewport. Left at the default, the same function
                  // flips the tooltip above the cursor and clamps it to `viewBox.y` -- pinned to
                  // the chart top, ~130px of overhang instead of ~330px. Review by Vadim.
                  wrapperStyle={{ zIndex: 50 }}
                />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                {brands.map((b, i) => (
                  <Line
                    key={b}
                    type="monotone"
                    dataKey={b}
                    stroke={lineColor(b, tracked, i)}
                    strokeWidth={b === tracked ? 2.5 : 1.5}
                    // AUT-481: a series with a single month of data can draw no line, so with
                    // `dot={false}` it was invisible until hovered. Those series get a dot; the
                    // rest keep none, which is what stops a 16-brand chart turning into confetti.
                    dot={singlePointBrands.has(b) ? { r: 3, fill: lineColor(b, tracked, i), strokeWidth: 0 } : false}
                    activeDot={{ r: 4 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Wrap a string into lines of ~maxChars for the axis sublabel.
function wrapText(s: string, maxChars = 16): string[] {
  const words = s.split(" ");
  const lines: string[] = [];
  let cur = "";
  for (const w of words) {
    if ((cur + " " + w).trim().length > maxChars) {
      if (cur) lines.push(cur.trim());
      cur = w;
    } else cur = `${cur} ${w}`;
  }
  if (cur) lines.push(cur.trim());
  return lines.slice(0, 3);
}

function PromptTypeTick(props: { x?: number; y?: number; payload?: { value: string }; exampleByType?: Record<string, string> }) {
  const { x = 0, y = 0, payload, exampleByType = {} } = props;
  const type = payload?.value ?? "";
  const example = exampleByType[type] ?? "";
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={14} textAnchor="middle" fontSize={11} fontWeight={500} className="fill-foreground">
        {titleCaseWords(type)}
      </text>
      {wrapText(example).map((line, i) => (
        <text key={i} x={0} y={34 + i * 12} textAnchor="middle" fontSize={10} className="fill-muted-foreground">
          {line}
        </text>
      ))}
    </g>
  );
}

// AUT-358 #5: a compact per-platform outcome chip for the consolidated Prompt-detail row.
function PlatformResultChip({ p }: { p: BiAiPrompt }) {
  const state = !p.usable ? "unusable" : p.brand_appeared ? "appeared" : "missed";
  const tone =
    state === "appeared"
      ? "bg-status-success-soft text-foreground"
      : state === "missed"
        ? "bg-status-error-soft text-foreground"
        : "bg-muted text-muted-foreground";
  const label = state === "unusable" ? p.unusable_reason ?? "unusable" : titleCase(p.platform);
  return (
    <span className={cn("inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium", tone)} title={`${titleCase(p.platform)}: ${state}`}>
      {state === "appeared" ? <Check className="h-3 w-3" /> : state === "missed" ? <X className="h-3 w-3" /> : null}
      {platformCode(p.platform)}
      {state === "unusable" && <span className="font-normal opacity-80">· {label}</span>}
    </span>
  );
}

function CardHeading({ title, tip }: { title: string; tip: string }) {
  return (
    <h3 className="flex items-center gap-1 text-xl font-semibold text-foreground">
      {title}
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger className="cursor-help"><Info className="h-3.5 w-3.5 text-muted-foreground" /></TooltipTrigger>
          <TooltipContent className="max-w-xs">{tip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </h3>
  );
}

function RankedBar({ label, value, max, emphasized }: { label: string; value: number; max: number; emphasized?: boolean }) {
  return (
    <div className="grid grid-cols-[140px_1fr_44px] items-center gap-3">
      <TruncatedText className={cn("min-w-0 text-xs text-foreground", emphasized ? "font-semibold" : "font-normal")}>{label}</TruncatedText>
      <Progress value={max > 0 ? (value / max) * 100 : 0} className="h-2.5" />
      <span className="text-xs tabular-nums text-foreground text-right">{fmtPct(value)}</span>
    </div>
  );
}

export function AiVisibilityTab({ brandId }: { brandId: number }) {
  const { data, isLoading, isError } = useGetBrandIntelligenceAiVisibility(brandId);
  const saveSet = useSaveAiCompetitorSet();
  const [promptFilter, setPromptFilter] = useState<"all" | "appeared" | "missed">("all");
  const [mentionPlatform, setMentionPlatform] = useState<string>("all");
  // AUT-480: its own platform, so filtering one chart does not move the other.
  const [sovPlatform, setSovPlatform] = useState<string>("all");
  const [manageOpen, setManageOpen] = useState(false);
  const [chosen, setChosen] = useState<string[]>([]);
  const [expandedPrompts, setExpandedPrompts] = useState<Set<string>>(new Set());
  const [expandedGaps, setExpandedGaps] = useState<Set<string>>(new Set());
  const toggle = (set: Set<string>, key: string) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  };
  const openManage = () => {
    setChosen((data?.competitor_set ?? []).map((c) => c.brand));
    setManageOpen(true);
  };

  const promptTypeRates = useMemo(() => {
    if (!data) return [];
    const agg: Record<string, { appeared: number; total: number; example: string }> = {};
    for (const p of data.prompts) {
      if (!p.usable || !p.type) continue;
      (agg[p.type] ??= { appeared: 0, total: 0, example: p.query });
      agg[p.type].total++;
      if (p.brand_appeared) agg[p.type].appeared++;
    }
    return Object.entries(agg).map(([type, v]) => ({ type, value: v.total > 0 ? Math.round((v.appeared / v.total) * 100) : 0, example: v.example }));
  }, [data]);

  if (isLoading) return <div className="rounded-sm border border-dashed py-12 text-center text-sm text-muted-foreground">Loading AI visibility…</div>;
  if (isError || !data) return <div className="rounded-sm border border-dashed py-12 text-center text-sm text-status-error-strong">Could not load AI visibility.</div>;
  if (!data.built) return <div className="rounded-sm border border-dashed py-12 text-center text-sm text-muted-foreground">AI visibility hasn't been probed yet. Use Refresh to build it.</div>;

  const competitorSet = data.competitor_set ?? [];
  const sovMax = Math.max(data.brand_mention_rate ?? 0, ...competitorSet.map((c) => c.mention_rate), 1);
  const sentiment = data.sentiment ?? { positive: 0, neutral: 0, negative: 0 };
  const sentimentTotal = sentiment.positive + sentiment.neutral + sentiment.negative;

  // AUT-358 #5: group probes by question (within its category) so the table shows one row per
  // prompt with its per-platform results inline, instead of one flat row per (prompt, platform).
  const promptGroups = (() => {
    const byKey = new Map<string, PromptGroup>();
    for (const p of data.prompts) {
      const key = `${p.leaf_category_id}\0${p.query}`;
      let g = byKey.get(key);
      if (!g) {
        g = { key, query: p.query, type: p.type, category: p.category, platforms: [], appearedAny: false, usableAny: false, competitors: new Set<string>() };
        byKey.set(key, g);
      }
      g.platforms.push(p);
      if (p.brand_appeared) g.appearedAny = true;
      if (p.usable) g.usableAny = true;
      for (const c of p.competitors_cited) g.competitors.add(c);
    }
    return [...byKey.values()];
  })();

  const filteredPromptGroups = promptGroups.filter((g) =>
    promptFilter === "all" ? true : promptFilter === "appeared" ? g.appearedAny : g.usableAny && !g.appearedAny,
  );

  // AUT-358 #6: group gaps by category — one header with the category's dominant cause/action, the
  // individual missed queries listed underneath (collapsed by default).
  const gapGroups = (() => {
    const byKey = new Map<string, { key: string; category: string; gaps: BiAiGap[] }>();
    for (const g of data.gaps ?? []) {
      const key = g.leaf_category_id || g.category;
      let grp = byKey.get(key);
      if (!grp) {
        grp = { key, category: g.category, gaps: [] };
        byKey.set(key, grp);
      }
      grp.gaps.push(g);
    }
    return [...byKey.values()].map((grp) => ({
      ...grp,
      dominantCause: mode(grp.gaps.map((g) => g.cause)),
      dominantAction: mode(grp.gaps.map((g) => g.action)),
    }));
  })();

  return (
    <div className="space-y-6">
      <TabHeader
        title="AI Visibility"
        description="How often AI assistants mention the brand versus competitors."
        showDescription
      />

      {/* Block A: three cards */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr_280px] gap-6">
        <Card>
          <CardContent className="p-6">
            <CardHeading title="Mention rate by platform" tip={BI_TOOLTIPS.platformBreakdown} />
            <p className="text-xs text-muted-foreground mt-1">{data.brand} vs. competitor avg</p>
            <div className="mt-5 space-y-3">
              {data.platforms.map((p) => {
                const above = p.brand_mention_rate >= p.competitor_avg_rate;
                const delta = (p.brand_mention_rate - p.competitor_avg_rate).toFixed(1);
                return (
                  <div key={p.platform} className="grid grid-cols-[auto_1fr_auto_auto_auto] items-center gap-2">
                    <span className={cn("inline-flex h-7 w-7 items-center justify-center rounded text-[10px] font-semibold text-white", PLATFORM_TINT[p.platform] ?? "bg-slate-500")}>
                      {platformCode(p.platform)}
                    </span>
                    <TruncatedText className="min-w-0 text-xs text-foreground">{titleCase(p.platform)}</TruncatedText>
                    {/* §6.3: brand rank among tracked brands (brand + competitor set) on this platform. */}
                    <span className="text-[10px] text-muted-foreground tabular-nums w-14 text-left" title="Brand rank among tracked brands on this platform">
                      {p.brand_rank != null ? `#${p.brand_rank} / ${p.tracked_brands}` : "—"}
                    </span>
                    <span className="text-xs text-foreground tabular-nums w-10 text-left">{fmtPct(p.brand_mention_rate)}</span>
                    <div className={cn("flex items-center gap-1 text-[10px] tabular-nums w-20", above ? "text-status-success-strong" : "text-status-error-strong")}>
                      {above ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      {above ? "+" : ""}{delta}% vs avg
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <CardHeading title="AI Mention Share" tip={BI_TOOLTIPS.mentionShare} />
            <p className="text-xs text-muted-foreground mt-1">Over {data.usable_samples ?? 0} usable of {data.total_samples ?? 0} probes</p>
            <div className="mt-3 text-3xl font-semibold tracking-tight text-foreground tabular-nums">{fmtPct(data.mention_share)}</div>
            <div className="mt-5 space-y-3">
              <RankedBar label={data.brand} value={data.brand_mention_rate ?? 0} max={sovMax} emphasized />
              {competitorSet.map((c) => (
                <RankedBar key={c.brand} label={c.brand} value={c.mention_rate} max={sovMax} />
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <CardHeading title="AI competitor set" tip="Brands the tracked brand is measured against for AI mention share. Auto-suggested from AI mentions + landscape SOV; override manually." />
            <div className="mt-3 flex flex-wrap items-start gap-1.5">
              {competitorSet.length > 0 ? (
                competitorSet.map((c) => <Badge key={c.brand} variant="secondary">{c.brand}</Badge>)
              ) : (
                <p className="text-xs text-muted-foreground">No competitors surfaced yet.</p>
              )}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Button variant="ghost" size="sm" className="h-7 gap-1 px-0 text-xs" onClick={openManage}>
                <Settings2 className="h-3 w-3" /> Manage
              </Button>
              {data.competitor_set_overridden && <span className="text-[10px] text-muted-foreground">custom</span>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Section 6.2: manual competitor-set override */}
      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>AI competitor set</DialogTitle>
            <DialogDescription>Pick up to 5 brands to measure {data.brand}'s AI mention share against. Leave empty to auto-suggest.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] space-y-1 overflow-y-auto py-1">
            {(data.competitor_candidates ?? []).map((b) => {
              const checked = chosen.includes(b);
              const atMax = chosen.length >= 5 && !checked;
              return (
                <label key={b} className={cn("flex items-center gap-2 rounded px-2 py-1.5 text-sm", atMax && "opacity-50")}>
                  <Checkbox
                    checked={checked}
                    disabled={atMax}
                    onCheckedChange={() => setChosen((prev) => (prev.includes(b) ? prev.filter((x) => x !== b) : [...prev, b]))}
                  />
                  <span>{b}</span>
                </label>
              );
            })}
            {(data.competitor_candidates ?? []).length === 0 && <p className="text-xs text-muted-foreground">No candidate brands yet.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline-on-white" onClick={() => { setChosen([]); saveSet.mutate({ brandId, brands: [] }, { onSuccess: () => setManageOpen(false) }); }} className="rounded-lg">
              Reset to auto
            </Button>
            <Button onClick={() => saveSet.mutate({ brandId, brands: chosen }, { onSuccess: () => setManageOpen(false) })} disabled={saveSet.isPending} className="rounded-lg">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block B: trend line charts. Always rendered so the sections are visible; each chart shows
          a "not enough history yet" empty state until snapshots accrue over monthly refreshes,
          rather than the whole block vanishing (which read as missing sections). */}
      <div className="space-y-6">
        <TrendChart
          title="AI mention rate trend"
          tip="How often each brand is mentioned across probes, month over month. Filter by platform."
          data={data.trend?.mention?.[mentionPlatform] ?? data.trend?.mention?.["all"] ?? []}
          brands={data.trend?.brands ?? []}
          tracked={data.brand}
          platforms={data.trend?.platforms}
          activePlatform={mentionPlatform}
          onPlatform={setMentionPlatform}
        />
        <TrendChart
          title="AI share of voice trend"
          tip="Each brand's AI mention share, month over month. Filter by platform."
          data={data.trend?.sov?.[sovPlatform] ?? data.trend?.sov?.["all"] ?? []}
          brands={data.trend?.brands ?? []}
          tracked={data.brand}
          platforms={data.trend?.platforms}
          activePlatform={sovPlatform}
          onPlatform={setSovPlatform}
        />
      </div>

      {/* Block C: sentiment + prompt-type */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="p-6">
            <CardHeading title="AI brand sentiment" tip="Sentiment toward the brand across responses that mention it." />
            <p className="text-xs text-muted-foreground mt-1">Representative excerpts · across {sentimentTotal} responses mentioning {data.brand}</p>
            {!data.sentiment_samples || data.sentiment_samples.length === 0 ? (
              <p className="mt-5 text-xs text-muted-foreground">No sentiment captured yet.</p>
            ) : (
              <div className="mt-5 space-y-5">
                {data.sentiment_samples.map((s, i) => (
                  <div key={i} className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground">{s.query}</p>
                      <p className="mt-1 text-xs italic text-muted-foreground">“{s.excerpt}”</p>
                    </div>
                    <Badge className={cn("border-0 shrink-0 text-foreground", SENTIMENT_BADGE[s.sentiment] ?? "bg-muted")}>{titleCase(s.sentiment)}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <CardHeading title="Mention rate by prompt type" tip="Share of usable prompts per type where the brand appeared." />
            <p className="text-xs text-muted-foreground mt-1">% of prompts per type where {data.brand} appeared</p>
            <div className="mt-4 h-[340px] w-full">
              {promptTypeRates.length === 0 ? (
                <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No prompt data yet.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={promptTypeRates} margin={{ top: 10, right: 10, left: 0, bottom: 72 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
                    <XAxis dataKey="type" interval={0} tickLine={false} axisLine={false} height={88} tick={<PromptTypeTick exampleByType={Object.fromEntries(promptTypeRates.map((d) => [d.type, d.example]))} />} />
                    <YAxis domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} width={40} tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} stroke="hsl(var(--muted-foreground))" />
                    <ReTooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} labelFormatter={(label) => titleCaseWords(String(label))} formatter={(v: number) => [`${v}%`, "Mention rate"]} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {promptTypeRates.map((d) => <Cell key={d.type} fill={TYPE_COLOR[d.type] ?? "hsl(var(--chart-1))"} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Block D: prompt detail table */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardHeading title="Prompt detail" tip="Every probe query run this cycle." />
              <p className="text-xs text-muted-foreground mt-1">All prompts · all platforms</p>
            </div>
            <Tabs value={promptFilter} onValueChange={(v) => setPromptFilter(v as typeof promptFilter)}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="appeared">Appeared</TabsTrigger>
                <TabsTrigger value="missed">Missed</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="mt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Prompt</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Platforms</TableHead>
                  <TableHead>Competitors cited</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPromptGroups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      {promptFilter === "appeared"
                        ? `No prompts where ${data.brand} appeared.`
                        : promptFilter === "missed"
                          ? `No prompts where ${data.brand} was missed.`
                          : "No prompts this cycle."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPromptGroups.map((g) => {
                    const open = expandedPrompts.has(g.key);
                    const competitors = [...g.competitors];
                    return (
                      <Fragment key={g.key}>
                        <TableRow className="cursor-pointer" onClick={() => setExpandedPrompts((s) => toggle(s, g.key))}>
                          <TableCell className="align-top text-muted-foreground">
                            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </TableCell>
                          <TableCell className="text-foreground max-w-[360px] align-top">
                            <TruncatedText>{g.query}</TruncatedText>
                            <p className="mt-0.5 text-[11px] text-muted-foreground">{g.category}</p>
                          </TableCell>
                          <TableCell className="align-top">
                            {g.type ? <Badge className={cn("border-0 text-foreground", TYPE_BADGE[g.type] ?? "bg-muted")}>{titleCaseWords(g.type)}</Badge> : <span className="text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell className="align-top">
                            <div className="flex flex-wrap gap-1">
                              {g.platforms.map((p, i) => (
                                <PlatformResultChip key={`${p.platform}-${i}`} p={p} />
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="align-top text-muted-foreground">{competitors.length > 0 ? competitors.join(", ") : "—"}</TableCell>
                        </TableRow>
                        {open && (
                          <TableRow className="bg-muted/30 hover:bg-muted/30">
                            <TableCell />
                            <TableCell colSpan={4} className="py-3">
                              <div className="space-y-2">
                                {g.platforms.map((p, i) => (
                                  <div key={`${p.platform}-${i}`} className="grid grid-cols-[90px_120px_1fr] items-start gap-3 text-xs">
                                    <span className="font-medium text-foreground">{titleCase(p.platform)}</span>
                                    <span>
                                      {!p.usable ? (
                                        <span className="text-muted-foreground">{p.unusable_reason ?? "unusable"}</span>
                                      ) : p.brand_appeared ? (
                                        <span className="inline-flex items-center gap-1 text-status-success-strong"><Check className="h-3.5 w-3.5" /> Appeared</span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1 text-status-error-strong"><X className="h-3.5 w-3.5" /> Missed</span>
                                      )}
                                    </span>
                                    <span className="text-muted-foreground">
                                      {p.sentiment && <span className="mr-3 capitalize">Sentiment: {p.sentiment}</span>}
                                      {p.competitors_cited.length > 0 ? `Competitors: ${p.competitors_cited.join(", ")}` : ""}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Block E: gap analysis — grouped by category (AUT-358 #6) */}
      {gapGroups.length > 0 && (
        <Card>
          <CardContent className="p-6">
            <CardHeading title="Why these gaps exist — and what to do about them" tip="Likely cause + recommended action for queries where the brand is missing or negative, grouped by category." />
            <p className="text-xs text-muted-foreground mt-1">Grouped by category · dominant cause and action shown per category</p>
            <div className="mt-5 space-y-3">
              {gapGroups.map((grp) => {
                const Icon = GAP_ICON[grp.dominantCause ?? ""] ?? Globe;
                const open = expandedGaps.has(grp.key);
                return (
                  <div key={grp.key} className="rounded-lg border border-border bg-card">
                    <button
                      type="button"
                      className="flex w-full items-start gap-4 p-4 text-left"
                      onClick={() => setExpandedGaps((s) => toggle(s, grp.key))}
                    >
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-semibold text-foreground">{grp.category}</p>
                          {grp.dominantCause && <Badge className="border-0 text-foreground bg-status-info-soft capitalize">{humanize(grp.dominantCause)}</Badge>}
                          {grp.dominantAction && <Badge className={cn("border-0 text-foreground capitalize", GAP_ACTION_BADGE[grp.dominantAction] ?? "bg-muted")}>{humanize(grp.dominantAction)}</Badge>}
                          <span className="text-xs text-muted-foreground">{grp.gaps.length} {grp.gaps.length === 1 ? "query" : "queries"}</span>
                        </div>
                      </div>
                      <span className="shrink-0 text-muted-foreground">{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</span>
                    </button>
                    {open && (
                      <div className="space-y-3 border-t border-border px-4 pb-4 pt-3">
                        {grp.gaps.map((g, i) => (
                          <div key={`${g.query}-${i}`} className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-medium text-foreground">{g.query}</p>
                              {g.cause && g.cause !== grp.dominantCause && <Badge className="border-0 text-foreground bg-status-info-soft capitalize">{humanize(g.cause)}</Badge>}
                              {g.action && g.action !== grp.dominantAction && <Badge className={cn("border-0 text-foreground capitalize", GAP_ACTION_BADGE[g.action] ?? "bg-muted")}>{humanize(g.action)}</Badge>}
                            </div>
                            {g.reasoning && <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{g.reasoning}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
